import { Data, Modal as ModalModule, UI, Utils } from 'common';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import ReactToPrint from 'react-to-print';
import HistoryCommunication from '../utils/HistoryCommunication';

const {
	Spinner,
	WrappedCheckboxWithLabel,
	WrappedCalendarWithLabel,
	WrappedDropdownWithLabel,
	InlineSpinner,
	Link,
} = UI;

const { Session, Util } = Utils;
const { Modal, ModalContext, ModalButtonActionType } = ModalModule;

const { Capability } = Data;

class CreateScanFormModal extends React.Component {
	constructor(props) {
		super(props);

		const { data = {} } = this.props;
		const { isForAllEligible = false, selectedPrints = [] } = data;
		const {
			eligiblePrints = [],
			originZip = null,
			shipDate = new Date(),
		} = !isForAllEligible ? this.processSelectedPrints(selectedPrints) : {};

		this.state = {
			isForAllEligible,
			eligiblePrints,
			shipDate,
			originZip,
			shouldPrintDetails: false,
			isLoading: isForAllEligible === true,
			isFetching: false,
			zipOptions: [],
			scanFormUrls: [], // scan form urls
			selectedPrints,
		};

		// the button is used to trigger printing feature in browsers
		this.scanformImgRef = null;
		this.reactToPrintButton = React.createRef();
	}

	componentDidMount() {
		const { isForAllEligible } = this.state;

		// true means that we need to call the endpoint to get all eligible prints
		if (isForAllEligible) {
			this.getEligiblePrints();
		}
	}

	/**
	 * Get only eligible prints to print scan forms
	 * @param {array} prints process selected prints
	 * @returns {object} eligiblePrints, originZip, shipDate
	 */
	processSelectedPrints = (prints = []) =>
		prints.reduce(
			(_result, print) => {
				const printDetails = print.printDetails || {};

				/**
				 * Following conditions have to be met in order to print scan form:
				 * (1) eligibleForManifest is `true`
				 * (2) same originZip
				 * (3) same shipDate
				 */
				if (printDetails.eligibleForManifest === true) {
					_result.eligiblePrints.push(print);

					// get default originZip
					if (!_result.originZip) {
						_result.originZip = printDetails.fromAddress?.postalCode;
					}

					// get default shipDate
					if (!_result.shipDate) {
						_result.shipDate = printDetails.fromAddress?.shipDate;
					}
				}

				return _result;
			},
			{
				eligiblePrints: [],
				originZip: null,
				shipDate: null,
			}
		);

	getEligiblePrints = async () => {
		const { data = {} } = this.props;
		const { originZip, shipDate } = this.state;
		const { fetchPrints } = data;
		const startDate = moment(shipDate);
		const endDate = moment(shipDate);

		endDate.add(1, 'day');

		if (!isFunction(fetchPrints)) {
			return;
		}

		const response = await fetchPrints({
			ship_date_start: `${startDate.format('YYYY-MM-DD')}T00:00:00`,
			ship_date_end: `${endDate.format('YYYY-MM-DD')}T00:00:00`,
			eligible_for_manifest: true,
			max_results: 500,
		});

		// build all available origin zip options
		const zipOptions = response.prints.reduce(
			(_zipOptions, { printDetails = {} }) => {
				const zipCode = printDetails.fromAddress?.postalCode;

				if (zipCode && !_zipOptions.includes(zipCode)) {
					_zipOptions.push(zipCode);
				}

				return _zipOptions;
			},
			[]
		);

		// use first zip code if `originZip` does not exist in the new `zipOptison`
		const eligibleOriginZip =
			isString(originZip) && zipOptions.includes(originZip)
				? originZip
				: zipOptions[0];

		this.setState({
			isLoading: false,
			eligiblePrints: response.prints.filter(
				({ printDetails = {} }) =>
					// filter by postal code because all prints need to have the same Origin ZIP
					printDetails.fromAddress?.postalCode === eligibleOriginZip
			),
			zipOptions,
			originZip: eligibleOriginZip,
		});
	};

	/**
	 * Translate error message when Create SCAN Form API call fails
	 * @param {string} description which contains Server Error message
	 * @returns {string} translated message
	 */
	translateCreateScanFormErrorDescription = description => {
		if (!description) return 'An error occurred';
		if (
			description.indexOf('There are more than one shipping dates present') !==
			-1
		) {
			return 'Shipments have different shipping dates.';
		} else if (
			description.indexOf('At least one shipping date is before today') !== -1
		) {
			return 'Shipping dates are old.';
		} else if (
			description.indexOf('There are no eligible labels with that date') !== -1
		) {
			return 'There are no eligible labels for the selected date.';
		} else if (
			description.indexOf('Invalid Tracking Number') !== -1 ||
			description.indexOf(
				'Invalid Stamps transaction ID or Tracking Number'
			) !== -1
		) {
			return 'At least one selected label is not eligible for SCAN Form. To view eligible labels, navigate to the Eligible For > SCAN Form filter.';
		} else if (
			description.indexOf('There are more than one zip codes present') !== -1
		) {
			return 'The origin zip codes are different.';
		} else {
			return description;
		}
	};

	ineligiblePrint = () => {
		const { selectedPrints } = this.state;

		return selectedPrints.filter(
			({ printDetails = {} }) => printDetails.eligibleForManifest === false
		);
	};

	/**
	 * Make an API call to create manifest and another call to store manifest data.
	 */
	createManifest = async () => {
		const {
			modalId,
			data: { onRefreshGridAPI },
		} = this.props;
		const {
			shouldPrintDetails,
			originZip,
			eligiblePrints,
			isForAllEligible,
			selectedPrints,
		} = this.state;
		const { showError, closeModal, closeAll } = this.context;

		if (!isForAllEligible && this.ineligiblePrint().length > 0) {
			closeAll(() =>
				showError({
					data: {
						title: 'Error',
						description: this.translateCreateScanFormErrorDescription(
							'Invalid Tracking Number'
						),
						primaryButtonText: 'OK',
						handleType: 'custom',
					},
					closeCallback: () => {
						closeModal(modalId);
					},
				})
			);
			return;
		}

		// make an api call to create manifest
		const createManifestResponse = await HistoryCommunication.createManifest({
			label_options: {
				label_format: 'png',
				print_instructions: shouldPrintDetails,
			},
			label_ids:
				!isForAllEligible && this.ineligiblePrint().length > 0
					? selectedPrints.map(({ printDetails = {} }) => printDetails.labelId)
					: eligiblePrints.map(({ printDetails = {} }) => printDetails.labelId),
			from_address: this.buildFromAddress(),
		});

		if (!HistoryCommunication.isSuccessful(createManifestResponse)) {
			const error = this.parseErrorResponse(createManifestResponse);

			return showError({
				data: {
					// display the error code
					code:
						isNumber(error.errorCode) && !isNaN(error.errorCode)
							? error.errorCode
							: null,
					title: !isNumber(error.errorCode) ? 'Error' : null,
					description: `<p>${error.errorMessage}</p>`,
					primaryButtonText: 'OK',
					handleType: 'custom',
				},
				closeCallback: () => {
					closeModal(modalId);
				},
			});
		}

		const {
			manifestId,
			numberOfItemsInManifest,
			shipDate,
			labels = [],
		} = Util.toCamelCase(createManifestResponse.data) || {};
		const scanFormUrls = labels.map(({ href }) => href);

		// make an api call to store manifest data
		const saveManifestResponse = await HistoryCommunication.saveManifest({
			manifest_id: manifestId,
			date_created: new Date().toISOString(),
			origin_zip: originZip,
			number_of_items: numberOfItemsInManifest,
			ship_date: shipDate,
			urls: scanFormUrls.join(','),
		});

		if (!HistoryCommunication.isSuccessful(saveManifestResponse)) {
			return showError({
				response: saveManifestResponse,
				closeCallback: () => {
					closeModal(modalId);
				},
			});
		}

		this.setState(
			{
				scanFormUrls,
			},
			() => {
				// click the element to trigger ReactToPrint printing feature in the browser
				if (this.reactToPrintButton.current) {
					this.reactToPrintButton.current.click();
				}

				closeModal(modalId);
			}
		);
		// This timeout was put into place as a workaround since the server data isn't updated on the \prints response quickly enough.
		setTimeout(() => onRefreshGridAPI?.(), 1000);
	};

	/**
	 * Refetch eligible prints if zip code changes
	 */
	onOriginZipCodeChange = async ({ value }) => {
		this.setState(
			{
				originZip: value,
				isLoading: true,
			},
			this.getEligiblePrints
		);
	};

	parseErrorResponse = response => {
		const errorMessage = isString(response?.error?.errorMessage)
			? response.error.errorMessage
			: response.ErrorDescription;

		return {
			errorCode: response?.error?.errorCode,
			errorMessage: this.translateCreateScanFormErrorDescription(errorMessage),
		};
	};

	buildFromAddress = () => {
		const { eligiblePrints, selectedPrints, isForAllEligible } = this.state;
		const useSelectedPrints =
			!isForAllEligible &&
			isArray(selectedPrints) &&
			this.ineligiblePrint().length > 0;
		const print = useSelectedPrints ? selectedPrints[0] : eligiblePrints?.[0];

		if (print) {
			const { printDetails = {} } = print;
			const { fromAddress = {} } = printDetails;

			return {
				company_name: fromAddress.companyName,
				name: fromAddress.name,
				address_line1: fromAddress.addressLine1,
				address_line2: fromAddress.addressLine2,
				address_line3: fromAddress.addressLine3,
				city: fromAddress.city,
				state_province: fromAddress.stateProvince,
				postal_code: `${fromAddress.postalCode}${
					fromAddress?.postalCodeAddon ? `-${fromAddress.postalCodeAddon}` : ''
				}`,
				country_code: fromAddress.countryCode,
				phone: fromAddress.phone,
				email: fromAddress.email,
			};
		}

		return null;
	};

	onPrintScanFormClick = () => {
		this.setState(
			{
				isFetching: true,
			},
			this.createManifest
		);
	};

	onInputChange = ({ target }) => {
		const { name, value, type, checked } = target;

		this.setState({
			[name]: type === 'checkbox' ? checked : value,
		});
	};

	/**
	 * This function will handle getPrints Api calls
	 * when date is changed and update eligiblePrints accordingly
	 */
	onShipDateChange = async ({ value }) => {
		this.setState(
			{
				shipDate: value,
				isLoading: true,
			},
			this.getEligiblePrints
		);
	};

	render() {
		const { modalId } = this.props;
		const {
			shipDate,
			originZip,
			isLoading,
			isFetching,
			shouldPrintDetails,
			eligiblePrints,
			zipOptions,
			isForAllEligible,
			scanFormUrls,
		} = this.state;
		const eligiblePackagesCount = eligiblePrints.filter(
			({ printDetails = {} }) =>
				printDetails.fromAddress?.postalCode === originZip
		).length;

		return (
			<Modal
				modalId={modalId}
				header="SCAN Form"
				className="history-scan-form create-scan-form-modal"
				primaryButton={{
					type: ModalButtonActionType.Ok,
					text: 'Print SCAN Form',
					onClick: this.onPrintScanFormClick,
					disabled: isFetching,
					dataqa: 'create-scan-form-print-scan-form-button',
				}}
				secondaryButton={{
					type: ModalButtonActionType.Cancel,
					text: 'Close',
					disabled: isFetching,
					dataqa: 'create-scan-form-close-button',
				}}
				size="small"
			>
				{isForAllEligible && (
					<>
						<p>
							<b>Select Shipments For</b>
						</p>
						<hr />
						<div className="form-container">
							{!Session.hasCapability(Capability.DelayedManifesting) && (
								<WrappedCalendarWithLabel
									label="Ship Date:"
									uiElementConfig={{
										type: 'date',
										value: shipDate,
										placeholder: 'MM/DD/YYYY',
										dataqa: 'ship-date',
										name: 'shipDate',
										minDate: new Date(),
										onChange: this.onShipDateChange,
									}}
								/>
							)}
							<div className="zip-loader-container">
								<WrappedDropdownWithLabel
									label="Origin Zip:"
									uiElementConfig={{
										name: 'originZip',
										value: originZip,
										options: zipOptions,
										dataqa: 'origin-zip',
										onChange: this.onOriginZipCodeChange,
										disabled: isLoading || eligiblePrints.length === 0,
									}}
								/>
								{isLoading && <InlineSpinner />}
							</div>
							<p className="eligible-packages-label">
								{eligiblePrints.length === 0 ? (
									<b> No Eligible Packages </b>
								) : (
									<b>
										Eligible Packages: {!isLoading && eligiblePackagesCount}
									</b>
								)}
								{isLoading && <InlineSpinner />}
							</p>
						</div>
						<hr />
					</>
				)}
				<div className="p-grid">
					<div className="p-col-12">
						<p>
							Please keep the items included in your SCAN form together, and
							present them with the SCAN form at the Post Office or to your mail
							carrier.
						</p>
						<p>Note: Transactions can only appear on one SCAN form.</p>
						<Link
							href={
								'https://help.stamps.com/hc/en-us/articles/20820467857051-Create-a-USPS-SCAN-Form'
							}
							target="_blank"
							dataqa="create-scan-learn-more-link"
							navIcon
						>
							Learn More about USPS SCAN Form
						</Link>
					</div>
					<div className="p-col-12">
						<WrappedCheckboxWithLabel
							className="checkbox"
							label="Also print instructions and details."
							showLabelAfter
							uiElementConfig={{
								checked: shouldPrintDetails,
								dataqa: 'print-instructions-checkbox',
								name: 'shouldPrintDetails',
								onChange: this.onInputChange,
							}}
						/>
					</div>

					{isFetching && (
						<Spinner msg="Generating labels. This might take a while." />
					)}

					<ReactToPrint
						trigger={() => (
							// NOTE: this button is used to trigger the printing feature in browser.
							// Do NOT pass an `onClick` prop as it will be overwritten.
							<button
								type="button"
								style={{ display: 'none' }}
								ref={this.reactToPrintButton}
							>
								Print
							</button>
						)}
						content={() => this.scanFormImgRef}
					/>

					{/* The following component is intended only for printing and should not render in the view */}
					<div style={{ display: 'none' }}>
						<div ref={el => (this.scanFormImgRef = el)}>
							{scanFormUrls.map((url, i) => (
								<img
									key={i}
									alt="scanform"
									className="history-scan-form scan-form"
									src={url}
								/>
							))}
						</div>
					</div>
				</div>
			</Modal>
		);
	}
}

CreateScanFormModal.propTypes = {
	data: PropTypes.shape({
		selectedPrints: PropTypes.array,

		// If the modal is open from Create SCAN Form > All Eligibles Packages this should be true otherwise false
		isForAllEligible: PropTypes.bool,
	}),
};

CreateScanFormModal.contextType = ModalContext;

export default CreateScanFormModal;
